import React, { useState, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { processPayment, getBoughtItems } from '../../api/vehicleAPI';
import Card from '../common/Card';
import Button from '../common/Button';
import { FaCreditCard, FaLock, FaQrcode, FaBarcode, FaWallet, FaShieldAlt, FaUserLock, FaBolt, FaSpinner } from 'react-icons/fa';
import { redirectToQRCode } from '../../utils/redirectUtils';

// --- STYLED COMPONENTS (sem alterações) ---
const FormCard = styled(Card)`
  padding: 30px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 25px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  &::after {
    content: "*";
    color: ${({ theme }) => theme.colors.danger};
    margin-left: 4px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-family: 'Manrope', sans-serif;
  font-size: 16px;
  transition: ${({ theme }) => theme.transitions.fast};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(108, 92, 231, 0.2);
  }
`;

const PaymentMethods = styled.div`
  margin: 30px 0;
  h3 {
    margin-bottom: 15px;
    color: ${({ theme }) => theme.colors.dark};
    display: flex;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const PaymentOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentOption = styled.div`
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.radii.sm};
  text-align: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  position: relative;

  &:hover { border-color: ${({ theme }) => theme.colors.primaryLight}; }
  &.selected {
    border-color: ${({ theme }) => theme.colors.primary};
    background: rgba(108, 92, 231, 0.1);
  }
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &::after {
      content: "Em breve";
      position: absolute;
      top: -8px; right: -8px;
      background: ${({ theme }) => theme.colors.warning};
      color: white;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: ${({ theme }) => theme.radii.sm};
      font-weight: bold;
    }
  }

  svg {
    font-size: 24px;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.primary};
  }
  p { font-size: 14px; font-weight: 600; }
`;

const InfoBoxes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 30px 0;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  svg {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 24px;
    margin-top: 3px;
  }
`;

const SecurePayment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  color: ${({ theme }) => theme.colors.gray};
  font-size: 14px;
  svg { color: ${({ theme }) => theme.colors.success}; }
`;

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(FaSpinner)`
  animation: ${spinAnimation} 1s linear infinite;
`;

const PaymentForm = ({ orderId }) => {
  const navigate = useNavigate();
  const { vehicleQuery, cart } = useContext(AppContext);
  const [formData, setFormData] = useState({ name: '', email: '', cpf: '' });
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .slice(0, 14);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    const finalValue = id === 'cpf' ? formatCPF(value) : value;
    setFormData(prev => ({ ...prev, [id]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const itensFiltrados = orderId
        ? cart.filter(item => item.id !== 'CONSULTA_COMPLETA')
        : cart;

      const itemIds = itensFiltrados.map(item => item.id);
      const prices = itensFiltrados.map(item => Number(item.price));

      const payload = { 
        customer: formData,
        itens: itemIds,
        prices: prices,
        placa: vehicleQuery.placa,
      };

      console.log("Dados do pagamento:", payload);

      const result = await processPayment(payload);

      if (result.success) {
        const orderId = result.orderId || result.id || result.order_id;

        const produtos = itensFiltrados.map(item => ({
          id: item.id,
          title: item.title,
          price: item.price
        }));

      redirectToQRCode(navigate, vehicleQuery.placa, null, {
        ...result,
        produtos
      });

      } else {
        throw new Error(result.message || "Não foi possível gerar a cobrança.");
      }

    } catch (error) {
      console.error("Payment failed", error);
      alert("Ocorreu um erro ao gerar o pagamento. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormCard>
      <Title><FaCreditCard /> Dados para Pagamento</Title>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Nome Completo</Label>
          <Input type="text" id="name" required placeholder="Digite seu nome completo" onChange={handleChange} value={formData.name} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">E-mail</Label>
          <Input type="email" id="email" required placeholder="Digite seu melhor e-mail" onChange={handleChange} value={formData.email}/>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="cpf">CPF</Label>
          <Input type="text" id="cpf" required placeholder="Digite seu CPF" onChange={handleChange} value={formData.cpf}/>
        </FormGroup>
        
        <PaymentMethods>
          <h3><FaWallet /> Método de Pagamento</h3>
          <PaymentOptions>
            <PaymentOption className="disabled"><FaCreditCard /><p>Cartão</p></PaymentOption>
            <PaymentOption className={paymentMethod === 'pix' ? 'selected' : ''} onClick={() => setPaymentMethod('pix')}><FaQrcode /><p>PIX</p></PaymentOption>
            <PaymentOption className="disabled"><FaBarcode /><p>Boleto</p></PaymentOption>
          </PaymentOptions>
        </PaymentMethods>

        <InfoBoxes>
            <InfoBox><FaShieldAlt /><div><h4>Pagamento 100% Seguro</h4><p>Seus dados são criptografados.</p></div></InfoBox>
            <InfoBox><FaUserLock /><div><h4>LGPD</h4><p>Respeitamos a Lei Geral de Proteção de Dados.</p></div></InfoBox>
            <InfoBox><FaBolt /><div><h4>Entrega Imediata</h4><p>Relatório liberado após confirmação do PIX.</p></div></InfoBox>
        </InfoBoxes>
        
        <Button type="submit" disabled={isSubmitting} style={{ width: '100%', fontSize: '18px', padding: '16px', marginTop: '20px' }}>
          {isSubmitting ? <FaSpinner spin /> : <FaLock />} {isSubmitting ? 'Processando...' : 'Finalizar com PIX'}
        </Button>

        <SecurePayment>
          <FaShieldAlt /><span>Pagamento seguro - Seus dados estão protegidos</span>
        </SecurePayment>
      </form>
    </FormCard>
  );
};

export default PaymentForm;
